import { Card, IndexTable, Page, Text } from '@shopify/polaris';
import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { getReportsInvoiced } from '../../../services/reports/sellsService';
import { useCurrency } from '../../../customHooks/useCurrency';
import { useTimezone } from '../../../customHooks/useTimezone';
import TotalizeNumbers from '../../UI/Reports/TotalizeNumbers';
import { VerticalSpace } from '../../UI/Reports/VerticalSpace';
import HeadSection from '../../UI/Reports/HeadSection';
import { FilterDatesCategories } from '../../UI/Reports/FilterDatesCategories';
// import BarChart from '../../UI/Graphs/BarChart';

// Lista de "M/YYYY" civiles en `tz` entre dos timestamps ISO (o Date).
const monthRangeInTz = (sinceIso, untilIso, tz) => {
  const start = DateTime.fromISO(String(sinceIso), { setZone: true }).setZone(tz).startOf('month');
  const end = DateTime.fromISO(String(untilIso), { setZone: true }).setZone(tz).startOf('month');
  if (!start.isValid || !end.isValid) return [];
  const months = [];
  let cursor = start;
  while (cursor <= end) {
    months.push(`${cursor.month}/${cursor.year}`);
    cursor = cursor.plus({ months: 1 });
  }
  return months;
};

const ViewReportInvoiced = () => {
  const [values, setValues] = useState(null);

  const getReports = (filters) =>
    // groupTz ya no se envía: el back asume MX por defecto (negocio mexicano).
    getReportsInvoiced({
      ...Object.keys(filters)
        .map((k) => (filters[k].value ? { slug: k, value: filters[k].value } : null))
        .filter((f) => f)
        .reduce((acc, f) => ({ ...acc, [f.slug]: f.value }), {}),
    }).then((r) => setValues(r.data));

  return (
    <Page title="Análisis de facturación" subtitle="">
      <>
        <FilterDatesCategories handleFiltersChange={getReports} isReady={() => null} />
        {values && (
          <>
            <VerticalSpace size="large" />
            <HeadSection title="FACTURACIÓN POR CLIENTE" subtitle="Unidades vendidas por centro y serie" />
            <VerticalSpace />
            <Card roundedAbove="sm">
              <Numbers values={values} />
            </Card>
            <VerticalSpace />
            <Card>
              <Table values={values} />
            </Card>
            <VerticalSpace />
            {/* <Card>
              <BarChart values={values} />
            </Card> */}
            <VerticalSpace size="xx-large" />
          </>
        )}
      </>
    </Page>
  );
};

const Numbers = ({ values }) => {
  const { currency } = useCurrency();
  const { metadata } = values;

  const numbers = [
    {
      label: 'Cantidad de productos',
      value: Intl.NumberFormat('es-Es').format(metadata?.qty) + ' Uds.',
    },
    {
      label: 'Ventas en PVF',
      value: Intl.NumberFormat('es-Es', {
        style: 'currency',
        currency: currency.symbol,
      }).format(metadata?.pvf * currency.exchange),
    },
    {
      label: 'Referencias de Colección',
      value: Intl.NumberFormat('es-Es').format(metadata?.count) + ' Serie-Color',
    },
  ];

  return <TotalizeNumbers rows={numbers} />;
};

const TableRow = ({ first, last, middleRows, index, id, position }) => (
  <IndexTable.Row id={id} key={id} position={position} index={index} subdued selected={false}>
    <IndexTable.Cell>{first}</IndexTable.Cell>
    {middleRows.map((r) => (
      <IndexTable.Cell>{r}</IndexTable.Cell>
    ))}
    <IndexTable.Cell>{last}</IndexTable.Cell>
  </IndexTable.Row>
);

const Textify = ({ value }) => (
  <Text variant="bodyMd" fontWeight="bold" as="span">
    {value}
  </Text>
);

const Table = ({ values }) => {
  const [brands, setBrands] = useState([]);
  const [months, setMonths] = useState([]);
  const [rowMarkup, setRowMarkup] = useState([]);
  const { currency } = useCurrency();
  const { timezone } = useTimezone();

  useEffect(() => {
    const b = [...new Set(values.data.map(({ customer }) => JSON.stringify(customer)))].map(JSON.parse);
    const m = monthRangeInTz(values.metadata.since, values.metadata.until, timezone);
    setBrands(b);
    setMonths(m);

    const Last = ({ data, id }) => {
      const totalPvf = data
        .filter(({ customer }) => customer.id === id)
        .reduce((acc, cur) => acc + parseFloat(cur.pvf), 0);

      return (
        <Textify
          value={Intl.NumberFormat('es-Es', {
            style: 'currency',
            currency: currency.symbol,
          }).format(Math.round(totalPvf) * currency.exchange)}
        />
      );
    };

    const getRows = ({ months, values, id }) =>
      months.map((my) => {
        const [m, y] = my.split('/').map((e) => parseInt(e));
        const monthValue =
          values.data.find(({ month, year, customer }) => month === m && year === y && customer.id === id)?.pvf ?? 0;
        return Intl.NumberFormat('es-Es', {
          style: 'currency',
          currency: currency.symbol,
        }).format(Math.round(monthValue) * currency.exchange);
      });

    setRowMarkup([
      ...brands.map(({ name, id }, index) => (
        <TableRow
          first={<Textify value={name} />}
          last={<Last data={values.data} id={id} />}
          middleRows={getRows({ months, values, id })}
          id={name}
          key={name}
          index={index}
        />
      )),
    ]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brands, months, values]);

  const totalRowMarkup = (
    <TableRow
      first={<Textify value="Total" />}
      last={
        <Textify
          value={Intl.NumberFormat('es-Es', {
            style: 'currency',
            currency: currency.symbol,
          }).format(Math.round(values.metadata.pvf) * currency.exchange)}
        />
      }
      middleRows={months.map((my) => {
        const [m, y] = my.split('/').map((e) => parseInt(e));
        const monthValue = values.data
          .filter(({ month, year }) => month === m && year === y)
          .reduce((acc, cur) => acc + parseFloat(cur.pvf), 0);
        return (
          <Textify
            value={Intl.NumberFormat('es-Es', {
              style: 'currency',
              currency: currency.symbol,
            }).format(Math.round(monthValue) * currency.exchange)}
          />
        );
      })}
      id="total"
      key="total"
      index={brands.length}
    />
  );

  return (
    <IndexTable
      resourceName={{ singular: 'reporte', plural: 'reportes' }}
      itemCount={brands.length}
      headings={[
        { title: 'Marca' },
        ...months.map((m) => ({ title: m })),
        {
          title: 'Total',
        },
      ]}
      selectable={false}
    >
      {rowMarkup}
      {totalRowMarkup}
    </IndexTable>
  );
};

export default ViewReportInvoiced;
